import React, { forwardRef, useCallback } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import db from '../../lib/db';
import { sortByRelevance } from '../../lib/sortByRelevance';
import { spammerLabel } from '../../lib/spammer';
import { escapeRegex } from '../../lib/stringUtils';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import { ProfileEntity } from '../ProfileEntity';
import { SpammerEntity } from '../SpammerEntity';
import { SuggestionList } from './SuggestionList';

export async function fetchMentionableEntities(query, channelId, workspaceId) {
  if (!workspaceId) return [];

  // An empty query (bare "@") matches everything and falls through to the default list below.
  const regex = new RegExp(escapeRegex(query || ''), 'i');

  // only suggest active profiles (excludes deactivated)
  const profiles = await db.profiles.where('[workspace_id+role]').anyOf([workspaceId, 'admin'], [workspaceId, 'member']).toArray();

  const filtered = profiles.filter((p) => regex.test(p.full_name)).map((p) => ({ ...p, entity_type: 'profile' }));

  const spammerEntities = [{ id: 'everyone', entity_type: 'spammer' }]
    .map((s) => ({ ...s, label: spammerLabel(s.id) }))
    .filter((s) => regex.test(s.label));

  if (!query) {
    // Bare "@": @everyone first, then members A→Z — relevance needs a query to mean anything.
    filtered.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
    return [...spammerEntities, ...filtered].slice(0, 5);
  }

  const combined = [...spammerEntities, ...filtered];
  const sorted = sortByRelevance(combined, query, (item) => item.label ?? item.full_name);
  return sorted.slice(0, 5);
}

export const AuthorList = forwardRef(({ channelId, command, query }, ref) => {
  const { workspaceId, profileId: currentProfileId } = useRouteWorkspace();
  const items = useLiveQuery(() => fetchMentionableEntities(query, channelId, workspaceId), [channelId, query, workspaceId], []);
  // @everyone wears the chat's own avatar in the suggestion row.
  const channel = useLiveQuery(
    () => (workspaceId && channelId ? db.channels.get([workspaceId, channelId]) : null),
    [channelId, workspaceId],
    null,
  );

  const handleSelect = useCallback(
    (item) => {
      command(item);
    },
    [command],
  );

  const renderItem = useCallback(
    (item) => {
      if (item?.entity_type === 'spammer') return <SpammerEntity spammer={item} suggestion flexiconResource={channel || undefined} />;
      return <ProfileEntity profile={item} suggestion isCurrentProfile={item.id === currentProfileId} />;
    },
    [channel, currentProfileId],
  );

  return <SuggestionList ref={ref} items={items} onSelect={handleSelect} renderItem={renderItem} query={query} />;
});

AuthorList.displayName = 'AuthorList';
